/**
 * @fileoverview Custom Views Hook for managing saved view configurations.
 * Provides state management for custom views with localStorage and optional
 * server persistence. Supports creating, updating, deleting, and sharing views.
 * @module hooks/useCustomViews
 */

import { useState, useCallback, useEffect } from 'react';
import { api } from '@/services/api';

/** @constant {string} Storage key for localStorage persistence */
const STORAGE_KEY = 'wms_custom_views';

/**
 * Default view configuration used as fallback.
 * @constant {Object}
 */
const DEFAULT_VIEW = {
    id: 'default',
    name: 'Default View',
    isDefault: true,
    isSystem: true,
    filters: {},
    sorting: [],
    columns: [],
    groupBy: null
};

/**
 * Hook for managing custom views with persistence support.
 * Provides CRUD operations for views with localStorage and optional server sync.
 * @param {string} entityType - Entity type identifier (e.g., 'orders', 'tasks')
 * @param {Object} [options={}] - Configuration options
 * @param {string[]} [options.defaultColumns=[]] - Default visible columns
 * @param {boolean} [options.persistToServer=false] - Whether to sync with server
 * @param {string|null} [options.userId=null] - User ID for server persistence
 * @returns {Object} View management interface
 * @returns {Object[]} returns.views - Array of all views
 * @returns {Object} returns.currentView - Currently selected view
 * @returns {boolean} returns.isLoading - Loading state
 * @returns {string|null} returns.error - Error message if any
 * @returns {Function} returns.createView - Create a new view
 * @returns {Function} returns.updateView - Update an existing view
 * @returns {Function} returns.deleteView - Delete a view
 * @returns {Function} returns.selectView - Select a view as current
 * @returns {Function} returns.toggleFavorite - Toggle view favorite status
 * @returns {Function} returns.shareView - Toggle view sharing status
 * @returns {Function} returns.duplicateView - Duplicate an existing view
 * @example
 * const { views, currentView, createView, selectView } = useCustomViews('orders');
 */
export const useCustomViews = (entityType, options = {}) => {
    const { 
        defaultColumns: _defaultColumns = [],
        persistToServer = false,
        userId = null
    } = options;

    const [views, setViews] = useState([DEFAULT_VIEW]);
    const [currentView, setCurrentView] = useState(DEFAULT_VIEW);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const storageKey = `${STORAGE_KEY}_${entityType}`;

    useEffect(() => {
        const loadViews = async () => {
            setIsLoading(true);
            try {
                if (persistToServer && userId) {
                    const response = await api.get(`/api/v1/views/${entityType}`);
                    if (response.data?.views) {
                        setViews([DEFAULT_VIEW, ...response.data.views]);
                    }
                } else {
                    const stored = localStorage.getItem(storageKey);
                    if (stored) {
                        const parsed = JSON.parse(stored);
                        setViews([DEFAULT_VIEW, ...parsed.views.filter(v => !v.isSystem)]);
                        if (parsed.currentViewId) {
                            const current = parsed.views.find(v => v.id === parsed.currentViewId);
                            if (current) setCurrentView(current);
                        }
                    }
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        loadViews();
    }, [entityType, persistToServer, userId, storageKey]);

    const saveToStorage = useCallback((newViews, newCurrentView) => {
        const toStore = {
            views: newViews.filter(v => !v.isSystem),
            currentViewId: newCurrentView?.id
        };
        localStorage.setItem(storageKey, JSON.stringify(toStore));
    }, [storageKey]);

    const createView = useCallback(async (viewData) => {
        const newView = {
            ...viewData,
            id: viewData.id || `view_${Date.now()}`,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        try {
            if (persistToServer) {
                await api.post(`/api/v1/views/${entityType}`, newView);
            }
            
            const newViews = [...views, newView];
            setViews(newViews);
            setCurrentView(newView);
            saveToStorage(newViews, newView);
            
            return newView;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [views, entityType, persistToServer, saveToStorage]);

    const updateView = useCallback(async (viewData) => {
        const updatedView = {
            ...viewData,
            updatedAt: new Date().toISOString()
        };

        try {
            if (persistToServer) {
                await api.put(`/api/v1/views/${entityType}/${viewData.id}`, updatedView);
            }
            
            const newViews = views.map(v => v.id === viewData.id ? updatedView : v);
            setViews(newViews);
            
            if (currentView?.id === viewData.id) {
                setCurrentView(updatedView);
            }
            
            saveToStorage(newViews, currentView?.id === viewData.id ? updatedView : currentView);
            
            return updatedView;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [views, currentView, entityType, persistToServer, saveToStorage]);

    const deleteView = useCallback(async (viewId) => {
        try {
            if (persistToServer) {
                await api.delete(`/api/v1/views/${entityType}/${viewId}`);
            }
            
            const newViews = views.filter(v => v.id !== viewId);
            setViews(newViews);
            
            if (currentView?.id === viewId) {
                const defaultView = newViews.find(v => v.isDefault) || newViews[0];
                setCurrentView(defaultView);
                saveToStorage(newViews, defaultView);
            } else {
                saveToStorage(newViews, currentView);
            }
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [views, currentView, entityType, persistToServer, saveToStorage]);

    const selectView = useCallback((view) => {
        setCurrentView(view);
        saveToStorage(views, view);
    }, [views, saveToStorage]);

    const toggleFavorite = useCallback(async (view) => {
        return updateView({ ...view, isFavorite: !view.isFavorite });
    }, [updateView]);

    const shareView = useCallback(async (view) => {
        return updateView({ ...view, isShared: !view.isShared });
    }, [updateView]);

    const duplicateView = useCallback(async (view) => {
        const newView = {
            ...view,
            id: `view_${Date.now()}`,
            name: `${view.name} (Copy)`,
            isSystem: false,
            isDefault: false,
            isFavorite: false,
            isShared: false
        };
        return createView(newView);
    }, [createView]);

    return {
        views,
        currentView,
        isLoading,
        error,
        createView,
        updateView,
        deleteView,
        selectView,
        toggleFavorite,
        shareView,
        duplicateView,
        setCurrentView: selectView
    };
};

export default useCustomViews;
